import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faShoppingCart, 
  faHeart,
  faFilter,
  faSort,
  faStar,
  faEye,
  faSpinner,
  faExclamationTriangle,
  faArrowRight,
  faArrowLeft,
  faChevronDown,
  faBars,
  faTimes,
  faShoppingBag,
  faCrown,
  faGem,
  faMagic,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import BasketSidebar from '../../common/BasketSidebar';
import { woocommerceService } from '../../../services/woocommerceService';
import { useBasket } from '../../../contexts/BasketContext';

// Import placeholder images - replace with actual shop images
import retailBanner from '../../../assets/banner/retailBan2.webp';
import duchessHero from '../../../assets/duchessGarden.webp';
import duchessLogo from '../../../assets/ducchessCollection.webp';

const ShopContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
  
  /* Prevent horizontal scroll on small viewports */
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
`;

const FullscreenLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(1200px 600px at 50% -20%, rgba(157, 109, 73, 0.08), transparent),
    linear-gradient(180deg, #ffffff 0%, #faf7f3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 2000;
  gap: 1rem;
`;

const FullscreenLoaderText = styled.div`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: grid;
  place-items: center;
`;

const LoaderRing = styled.div`
  position: absolute;
  width: ${props => props.size || 180}px;
  height: ${props => props.size || 180}px;
  border-radius: 50%;
  border: 3px solid rgba(16, 29, 64, 0.08);
  border-top-color: ${props => props.color || props.theme.colors.accent};
  animation: spin ${props => props.duration || 1.4}s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoaderLogo = styled.img`
  width: 120px;
  height: auto;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.18));
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
    100% { transform: translateY(0); }
  }
`;

const ProgressContainer = styled.div`
  width: 220px;
  height: 6px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06) inset;
`;

const ProgressShimmer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(157, 109, 73, 0.35), transparent);
  background-size: 200% 100%;
  animation: progressShift 1.3s linear infinite;

  @keyframes progressShift {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const ContentLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
`;

// Slider Section
const SliderSection = styled.section`
  position: relative;
  height: 70vh;
  min-height: 500px;
  margin-bottom: 4rem;
  margin-top: -120px;
  padding-top: 120px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 320px;
    margin-top: -100px;
    padding-top: 100px;
  }
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => `url('${props.$image}') center/cover no-repeat`};
  transform: translateX(${props => props.$active ? '0%' : props.$direction});
  transition: transform 0.6s ease;
  will-change: transform;
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.60) 0%,
      rgba(0, 0, 0, 0.45) 60%,
      rgba(0, 0, 0, 0.35) 100%
    );
  }
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 4rem;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroCta = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
`;

const SliderControls = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 3;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? props.theme.colors.accent : 'rgba(255,255,255,0.7)'};
  cursor: pointer;
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$left ? 'left: 1rem;' : 'right: 1rem;'}
  z-index: 3;
  background: rgba(0,0,0,0.4);
  color: white;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
`;

// (Basket button moved to Navbar)

// Featured Categories Section
const FeaturedSection = styled.section`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(45deg, ${props => props.theme.colors.accent}, #d4a574);
    border-radius: 2px;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.light};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(16, 29, 64, 0.15);
    border-color: ${props => props.theme.colors.accent};
  }
`;

const CategoryImage = styled.div`
  height: 200px;
  background: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  position: relative;
  overflow: hidden;
`;

const CategoryInfo = styled.div`
  padding: 1.5rem;
  text-align: center;
`;

const CategoryName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const CategoryCount = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.9rem;
`;

// Enhanced Search and Filters
const SearchAndFilters = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 3rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  min-width: 0;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem 1rem 3.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 360px) {
    padding: 0.9rem 1.1rem 0.9rem 3rem;
    border-radius: 40px;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  & > * {
    flex: 0 0 auto;
  }
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
  
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 0.8rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FilterSelect = styled.select`
  padding: 1rem 1.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  background-color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 160px;
  white-space: nowrap;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
  
  @media (max-width: 768px) {
    min-width: 140px;
    padding: 0.8rem 1.2rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 1rem 1.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  background-color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 160px;
  white-space: nowrap;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
  
  @media (max-width: 768px) {
    min-width: 140px;
    padding: 0.8rem 1.2rem;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

// Enhanced Product Grid
const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Maximum 4 columns on any screen size */
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 3rem;
  }
  
  @media (min-width: 1200px) and (max-width: 1399px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1000px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 768px) and (max-width: 999px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.875rem;
  }
  
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
`;

const ProductCard = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  background: transparent;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  /* Disable hover-based scale on touch devices to prevent overflow */
  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
`;

const ProductImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${ProductCard}:hover & {
    transform: scale(1.1);
    filter: brightness(1.1) contrast(1.05);
  }

  @media (hover: none) {
    ${ProductCard}:hover & {
      transform: none;
      filter: none;
    }
  }
`;

const ProductImageSecondary = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 2;

  ${ProductCard}:hover & {
    opacity: 1;
    transform: scale(1.1);
    filter: brightness(1.1) contrast(1.05);
  }

  @media (hover: none) {
    ${ProductCard}:hover & {
      opacity: 0;
      transform: none;
      filter: none;
    }
  }
`;

const ProductOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0) 60%,
    rgba(0, 0, 0, 0.7) 100%
  );
`;

// Removed old on-image text styles (ProductInfo, ProductTitle, ProductPrice)

const ModernAddToCartButton = styled.button`
  width: 100%;
  padding: 0.875rem 1.5rem;
  background: ${props => props.added 
    ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
    : 'linear-gradient(135deg, #9d6d49 0%, #b8845c 100%)'
  };
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 16px rgba(157, 109, 73, 0.3);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(157, 109, 73, 0.4);
    background: ${props => props.added 
      ? 'linear-gradient(135deg, #45a049 0%, #4CAF50 100%)' 
      : 'linear-gradient(135deg, #b8845c 0%, #9d6d49 100%)'
    };
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Moved below AddToCartButton declaration

const ProductBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 15;
  background: linear-gradient(135deg, #9d6d49, #b8845c);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;

const ProductActions = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 15;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${ProductCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #101d40;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: #9d6d49;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(157, 109, 73, 0.4);
  }
`;

// Old styles removed - using new overlay design

// Under-image info styles
const CardInfo = styled.div`
  padding: 1rem 0.25rem 0.25rem 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
  overflow-wrap: anywhere;
  hyphens: auto;
`;

const CardPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Stars = styled.div`
  display: flex;
  gap: 3px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#FFD700' : '#E0E0E0'};
  font-size: 1rem;
  transition: color 0.2s ease;
`;

const RatingText = styled.span`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props => props.$added ? '#4CAF50' : props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.$added ? '#45a049' : props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
  
  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    color: #999;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Derived small CTA for image overlay
const OverlayAddToCartButton = styled(AddToCartButton)`
  width: auto;
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  box-shadow: 0 6px 14px rgba(0,0,0,0.18);
`;

// Loading and Error States
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem;
  flex-direction: column;
  gap: 1.5rem;
`;

const LoadingSpinner = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.accent};
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: #F44336;
`;

const ErrorText = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  max-width: 500px;
  font-weight: 500;
`;

const RetryButton = styled.button`
  padding: 1rem 2.5rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
`;

const NoProductsContainer = styled.div`
  text-align: center;
  padding: 6rem 2rem;
`;

const NoProductsText = styled.p`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  font-weight: 500;
`;

// Maintenance overlay (password gate)
const MaintenanceOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, #ffffff 0%, #faf7f3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 2rem;
`;

const MaintenanceCard = styled.div`
  width: 100%;
  max-width: 640px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  border: 1px solid ${props => props.theme.colors.greyBackground};
  padding: 2rem;
  text-align: center;
`;

const MaintenanceLogo = styled.img`
  width: 140px;
  height: auto;
  margin-bottom: 1rem;
`;

const MaintenanceTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  margin: 0.5rem 0 0.5rem 0;
`;

const MaintenanceText = styled.p`
  color: #666;
  margin: 0.5rem 0 1.25rem 0;
`;

const Dots = styled.div`
  display: inline-flex;
  gap: 6px;
  margin-bottom: 1rem;
  & span {
    width: 8px; height: 8px; border-radius: 50%; background: ${props => props.theme.colors.accent};
    animation: bounce 1.2s infinite ease-in-out;
  }
  & span:nth-child(2) { animation-delay: 0.15s; }
  & span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes bounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
    40% { transform: translateY(-6px); opacity: 1; }
  }
`;

const PasswordRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  justify-content: center;
  @media (max-width: 480px) { flex-direction: column; }
`;

const PasswordInput = styled.input`
  padding: 0.9rem 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 10px;
  font-size: 1rem;
  min-width: 260px;
`;

const UnlockButton = styled.button`
  padding: 0.9rem 1.25rem;
  border: none;
  border-radius: 10px;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-weight: 700;
  cursor: pointer;
`;

// Enhanced Pagination
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 4rem;
  flex-wrap: wrap;
  padding: 0 1rem;
  max-width: 100%;
  box-sizing: border-box;
`;

const PaginationButton = styled.button`
  padding: 1rem 1.8rem;
  border: 2px solid ${props => props.theme.colors.primary};
  background-color: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.primary};
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(16, 29, 64, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DuchessCollectionShop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToBasket, isInBasket, totalItems } = useBasket();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minLoadingTime, setMinLoadingTime] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [showCategories, setShowCategories] = useState(true);
  const [showBasket, setShowBasket] = useState(false);
  const [addedToCart, setAddedToCart] = useState({});
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const filtersRef = useRef(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');

  const productsPerPage = 12;
  // Load gate state
  useEffect(() => {
    try {
      const saved = localStorage.getItem('shopAccess_duchess');
      setIsUnlocked(saved === 'true');
    } catch (_) {}
  }, []);

  const handleUnlock = () => {
    if (password === '!QAZ2wsx') {
      setIsUnlocked(true);
      try { localStorage.setItem('shopAccess_duchess', 'true'); } catch (_) {}
    }
  };


  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setMinLoadingTime(false);
      setError(null);

      // Set minimum loading time to ensure spinner is visible
      const minTimePromise = new Promise(resolve => {
        setTimeout(() => {
          setMinLoadingTime(true);
          resolve();
        }, 800); // Minimum 800ms loading time
      });

      const options = {
        page: currentPage,
        per_page: productsPerPage,
        search: searchTerm,
        category: selectedCategory,
        orderby: sortBy,
        order: 'desc'
      };

      const productsData = await woocommerceService.getProducts(options);
      setProducts(productsData);
      
      // Calculate total pages (this is a simplified calculation)
      // In a real implementation, you'd get this from the API response headers
      setTotalPages(Math.ceil(productsData.length / productsPerPage));

      // Wait for minimum loading time before hiding spinner
      await minTimePromise;
    } catch (err) {

      setError('Failed to load products. Please try again.');
      // Still wait for minimum time even on error
      await new Promise(resolve => {
        setTimeout(() => {
          setMinLoadingTime(true);
          resolve();
        }, 800);
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const categoriesData = await woocommerceService.getCategories();
      setCategories(categoriesData);
    } catch (err) {

    }
  };

  // Fetch featured products
  const fetchFeaturedProducts = async () => {
    try {
      const featuredData = await woocommerceService.getFeaturedProducts({ per_page: 6 });
      setFeaturedProducts(featuredData);
    } catch (err) {

    }
  };

  // Build slides from categories (if they have images) with fallbacks
  useEffect(() => {
    const catSlides = (categories || [])
      .filter(c => c.image && c.image.src)
      .slice(0, 4)
      .map(c => ({
        image: c.image.src,
        title: c.name,
        subtitle: 'Explore the collection',
        to: `/duchess-collection?category=${c.id}`
      }));

    const fallback = [
      { image: duchessHero, title: 'The Duchess Collection', subtitle: 'Refined fashion and homeware', to: '/duchess-collection' },
      { image: retailBanner, title: 'New Arrivals', subtitle: 'Just landed pieces', to: '/duchess-collection?sort=date' }
    ];

    const merged = catSlides.length > 0 ? catSlides.concat(fallback.slice(0, Math.max(0, 3 - catSlides.length))) : fallback;
    setSlides(merged);
    setActiveSlide(0);
  }, [categories]);

  // Auto-advance slider
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(id);
  }, [slides]);

  // Parse query params (q, category, sale, sort, view)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const cat = params.get('category') || '';
    const sale = params.get('sale') === '1';
    const srt = params.get('sort') || 'date';
    const view = params.get('view') || '';

    setSearchTerm(q);
    setSelectedCategory(cat);
    setSortBy(srt);

    // Trigger fetches
    fetchCategories();
    fetchFeaturedProducts();
    fetchProducts();

    // If view=all, hide categories and scroll to filters
    if (view === 'all') {
      setShowCategories(false);
      // Scroll near filters with offset to account for fixed headers
      requestAnimationFrame(() => {
        if (filtersRef.current) {
          const rect = filtersRef.current.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const offset = window.innerWidth <= 768 ? 120 : 160; // header height offset
          window.scrollTo({ top: scrollTop + rect.top - offset, behavior: 'smooth' });
        }
      });
    } else {
      setShowCategories(true);
    }
  }, [location.search]);

  // Listen for global event to open basket (from Navbar)
  useEffect(() => {
    const handleOpenBasket = () => setShowBasket(true);
    document.addEventListener('openBasketSidebar', handleOpenBasket);
    return () => document.removeEventListener('openBasketSidebar', handleOpenBasket);
  }, []);

  // Search with debounce (and update URL). Also ensure view=all so results show prominently.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(true);
      setMinLoadingTime(false);
      const params = new URLSearchParams(location.search);
      if (searchTerm) params.set('q', searchTerm); else params.delete('q');
      params.set('view', 'all');
      navigate({ pathname: '/duchess-collection', search: params.toString() }, { replace: true });
      fetchProducts();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle category filter
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    if (value) params.set('category', value); else params.delete('category');
    navigate({ pathname: '/duchess-collection', search: params.toString() }, { replace: true });
  };

  // Handle sort
  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    if (value) params.set('sort', value); else params.delete('sort');
    navigate({ pathname: '/duchess-collection', search: params.toString() }, { replace: true });
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle add to cart
  const handleAddToCart = (product) => {
    addToBasket(product, 1);
    setAddedToCart({ ...addedToCart, [product.id]: true });
    setTimeout(() => {
      setAddedToCart({ ...addedToCart, [product.id]: false });
    }, 2000);
  };

  // Handle wishlist (placeholder)
  const handleWishlist = (product) => {

    // Implement wishlist functionality
    alert(`Added ${product.name} to wishlist!`);
  };

  // Handle product click - navigate to detail page
  const handleProductClick = (product) => {
    navigate(`/duchess-collection/product/${product.id}`);
  };

  // Handle quick view (placeholder)
  const handleQuickView = (product) => {

    // Implement quick view functionality
    alert(`Quick view for ${product.name}`);
  };

  // Get category icon
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('fashion') || name.includes('clothing')) return faCrown;
    if (name.includes('home') || name.includes('decor')) return faGem;
    if (name.includes('jewelry') || name.includes('accessories')) return faMagic;
    return faShoppingBag;
  };

  // Handle category click → route to all products with category filter
  const handleCategoryClick = (categoryId) => {
    navigate(`/duchess-collection?view=all&category=${categoryId}`);
  };

  // Render product rating
  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star key={i} filled={i < fullStars}>
          ★
        </Star>
      );
    }

    return (
      <ProductRating>
        <Stars>{stars}</Stars>
        <RatingText>({rating})</RatingText>
      </ProductRating>
    );
  };

  // Render product price (plain, for below-image content)
  const renderPrice = (product) => {
    if (product.sale_price && product.sale_price !== product.regular_price) {
      return (
        <>
          £{product.sale_price}
          <span style={{ textDecoration: 'line-through', opacity: 0.7, marginLeft: '0.5rem' }}>
            £{product.regular_price}
          </span>
        </>
      );
    }
    return <>£{product.price}</>;
  };

  if (loading && products.length === 0) {
    return (
      <>
        <FullscreenLoader>
          <LoaderWrapper>
            <LoaderRing size={180} duration={1.4} color={'#9d6d49'} />
            <LoaderRing size={140} duration={1.8} color={'#101d40'} />
            <LoaderRing size={100} duration={2.2} color={'#d4a574'} />
            <LoaderLogo src={duchessLogo} alt="The Duchess Collection" />
          </LoaderWrapper>
          <FullscreenLoaderText>Loading the Duchess Collection...</FullscreenLoaderText>
          <ProgressContainer>
            <ProgressShimmer />
          </ProgressContainer>
        </FullscreenLoader>
      </>
    );
  }

  if (error) {
    return (
      <ShopContainer>
        {/* Show maintenance overlay instead of error UI when backend fails */}
        {!loading && (
          <MaintenanceOverlay>
            <MaintenanceCard>
              <MaintenanceLogo src={duchessLogo} alt="The Duchess Collection" />
              <MaintenanceTitle>We’re crafting a new shopping experience</MaintenanceTitle>
              <MaintenanceText>
                Our team is working hard to bring you an exciting online store. We’re currently undergoing maintenance.
              </MaintenanceText>
              <Dots><span></span><span></span><span></span></Dots>
              <PasswordRow>
                <PasswordInput
                  type="password"
                  placeholder="Enter access password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <UnlockButton onClick={handleUnlock}>Unlock</UnlockButton>
              </PasswordRow>
            </MaintenanceCard>
          </MaintenanceOverlay>
        )}
        <Footer />
      </ShopContainer>
    );
  }

  return (
    <ShopContainer>
      {/* Basket handled by Navbar */}

      {/* Slider Hero Section */}
      <SliderSection>
        {slides.map((s, idx) => (
          <Slide key={idx} $image={s.image} $active={idx === activeSlide} $direction={idx < activeSlide ? '-100%' : '100%'}>
            <SlideContent>
              <div>
                <HeroTitle>{s.title}</HeroTitle>
                <HeroSubtitle>{s.subtitle}</HeroSubtitle>
                <HeroCta onClick={() => navigate('/duchess-collection?view=all')}>
                  Explore
                  <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
                </HeroCta>
              </div>
            </SlideContent>
          </Slide>
        ))}

        {slides.length > 1 && (
          <>
            <ArrowButton $left onClick={() => setActiveSlide(prev => (prev - 1 + slides.length) % slides.length)}>
              ‹
            </ArrowButton>
            <ArrowButton onClick={() => setActiveSlide(prev => (prev + 1) % slides.length)}>
              ›
            </ArrowButton>
            <SliderControls>
              {slides.map((_, i) => (
                <Dot key={i} $active={i === activeSlide} onClick={() => setActiveSlide(i)} />
              ))}
            </SliderControls>
          </>
        )}
      </SliderSection>
      
      <ContentContainer>
        {loading && products.length > 0 && !minLoadingTime && (
          <ContentLoadingOverlay>
            <LoadingSpinner>
              <FontAwesomeIcon icon={faSpinner} />
            </LoadingSpinner>
          </ContentLoadingOverlay>
        )}
        {/* Featured Categories Section */}
        {showCategories && categories.length > 0 && (
          <FeaturedSection>
            <SectionTitle>Shop by Category</SectionTitle>
            <CategoriesGrid>
              {categories.slice(0, 6).map(category => (
                <CategoryCard key={category.id} onClick={() => handleCategoryClick(category.id)}>
                  <CategoryImage>
                    <FontAwesomeIcon icon={getCategoryIcon(category.name)} />
                  </CategoryImage>
                  <CategoryInfo>
                    <CategoryName>{category.name}</CategoryName>
                    <CategoryCount>{category.count || 0} items</CategoryCount>
                  </CategoryInfo>
                </CategoryCard>
              ))}
            </CategoriesGrid>
          </FeaturedSection>
        )}

        {/* Featured Products Section */}
        {featuredProducts.length > 0 && !searchTerm && !selectedCategory && (
          <FeaturedSection>
            <SectionTitle>Featured Products</SectionTitle>
            <ProductsGrid>
              {featuredProducts.map(product => (
                <ProductCard key={product.id} onClick={() => handleProductClick(product)}>
                  <ProductImageContainer>
                    <ProductImage
                      src={product.images?.[0]?.src || '/placeholder-product.jpg'}
                      alt={product.name}
                      onError={(e) => {
                        e.target.src = '/placeholder-product.jpg';
                      }}
                    />
                    {product.images?.[1]?.src && (
                      <ProductImageSecondary
                        src={product.images?.[1]?.src}
                        alt={`${product.name} alternate view`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <ProductOverlay>
                      <ProductActions>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleWishlist(product);
                        }}>
                          <FontAwesomeIcon icon={faHeart} />
                        </ActionButton>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleQuickView(product);
                        }}>
                          <FontAwesomeIcon icon={faEye} />
                        </ActionButton>
                      </ProductActions>
                    </ProductOverlay>
                    <ProductBadge>Featured</ProductBadge>
                  </ProductImageContainer>
                  <CardInfo>
                    <CardTitle>{product.name}</CardTitle>
                    <CardPrice>{renderPrice(product)}</CardPrice>
                    <ModernAddToCartButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                    >
                      Add to Basket
                    </ModernAddToCartButton>
                  </CardInfo>
                </ProductCard>
              ))}
            </ProductsGrid>
          </FeaturedSection>
        )}

        {/* Search and Filters */}
        <SearchAndFilters ref={filtersRef}>
          <SearchContainer>
            <SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search our collection..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </SearchContainer>
          
          <FilterContainer>
            <FilterSelect value={selectedCategory} onChange={handleCategoryChange}>
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </FilterSelect>
            
            <SortSelect value={sortBy} onChange={handleSortChange}>
              <option value="date">Newest First</option>
              <option value="popularity">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </SortSelect>
          </FilterContainer>
        </SearchAndFilters>

        {/* Products Grid */}
        {products.length === 0 ? (
          <NoProductsContainer>
            <NoProductsText>
              {searchTerm ? `No products found for "${searchTerm}"` : 'No products available at the moment.'}
            </NoProductsText>
          </NoProductsContainer>
        ) : (
          <>
            <ProductsGrid>
              {products.map(product => (
                <ProductCard key={product.id} onClick={() => handleProductClick(product)}>
                  <ProductImageContainer>
                    <ProductImage
                      src={product.images?.[0]?.src || '/placeholder-product.jpg'}
                      alt={product.name}
                      onError={(e) => {
                        e.target.src = '/placeholder-product.jpg';
                      }}
                    />
                    {product.images?.[1]?.src && (
                      <ProductImageSecondary
                        src={product.images?.[1]?.src}
                        alt={`${product.name} alternate view`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <ProductOverlay>
                      <ProductActions>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleWishlist(product);
                        }}>
                          <FontAwesomeIcon icon={faHeart} />
                        </ActionButton>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleQuickView(product);
                        }}>
                          <FontAwesomeIcon icon={faEye} />
                        </ActionButton>
                      </ProductActions>
                    </ProductOverlay>
                    {product.sale_price && product.sale_price !== product.regular_price && (
                      <ProductBadge>Sale</ProductBadge>
                    )}
                    {product.featured && (
                      <ProductBadge>Featured</ProductBadge>
                    )}
                  </ProductImageContainer>
                  <CardInfo>
                    <CardTitle>{product.name}</CardTitle>
                    <CardPrice>{renderPrice(product)}</CardPrice>
                    <ModernAddToCartButton 
                      $added={addedToCart[product.id]}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                    >
                      {addedToCart[product.id] ? 'Added' : 'Add to Basket'}
                    </ModernAddToCartButton>
                  </CardInfo>
                </ProductCard>
              ))}
            </ProductsGrid>

            {totalPages > 1 && (
              <Pagination>
                <PaginationButton
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
                  Previous
                </PaginationButton>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <PaginationButton
                    key={page}
                    onClick={() => handlePageChange(page)}
                    active={page === currentPage}
                  >
                    {page}
                  </PaginationButton>
                ))}
                
                <PaginationButton
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
                </PaginationButton>
              </Pagination>
            )}
          </>
        )}
      </ContentContainer>
      
      <Footer />
      
      {/* Basket Sidebar */}
      <BasketSidebar 
        isOpen={showBasket} 
        onClose={() => setShowBasket(false)} 
      />

      {/* Maintenance Overlay shows AFTER initial load unless unlocked */}
      {!loading && !isUnlocked && (
        <MaintenanceOverlay>
          <MaintenanceCard>
            <MaintenanceLogo src={duchessLogo} alt="The Duchess Collection" />
            <MaintenanceTitle>We’re crafting a new shopping experience</MaintenanceTitle>
            <MaintenanceText>
              Our team is working hard to bring you an exciting online store. We’re currently undergoing maintenance.
            </MaintenanceText>
            <Dots><span></span><span></span><span></span></Dots>
            <PasswordRow>
              <PasswordInput
                type="password"
                placeholder="Enter access password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <UnlockButton onClick={handleUnlock}>Unlock</UnlockButton>
            </PasswordRow>
          </MaintenanceCard>
        </MaintenanceOverlay>
      )}
    </ShopContainer>
  );
};

export default DuchessCollectionShop;